import React from 'react';
import { withTranslation } from 'react-i18next';
import { Button, Form, FormInstance, Schema, toaster } from 'rsuite';
import { constant, util } from '@helper';
import _ from 'lodash';
import { ForgotPasswordPayload } from '@common/ApiTypes';
import constants from '@helper/constants';
import { FaArrowLeft } from 'react-icons/fa';
import { AuthAPI } from '@services/api';

const { withRouter } = util;
const { StringType } = Schema.Types;

// The page paints its own full-bleed background. `m-auto` on the content
// wrapper keeps it centered while still collapsing to the padding when the
// form is taller than the viewport, so it never gets clipped at the top.
const SCREEN_CLASS = [
  'flex min-h-screen flex-col px-4',
  'pt-[max(6rem,17.5vh)] pb-[max(1.5rem,3.5vh)]',
  'font-semibold leading-[1.4] text-[#212121]',
].join(' ');

// The background is a fixed viewport layer (not a class on the scroll
// container) so it still covers the visible area when the document is
// taller than this screen. It must NOT use a negative z-index:
// `.app`/`body` have white backgrounds, and a negative z-index layer would
// paint underneath them. Instead the content is given its own stacking
// context (relative z-0) so it paints above the layer, which comes first
// in DOM order.
const BACKGROUND_CLASS =
  'fixed inset-0 bg-candidate-login bg-cover bg-no-repeat';

const LAYOUT_CLASS = [
  'relative z-0 m-auto flex w-full max-w-5xl flex-col items-center gap-10',
  'md:flex-row md:justify-center',
].join(' ');

const PANEL_CLASS = 'w-full max-w-xl px-5';

type State = {
  forgotPass: ForgotPasswordPayload;
};

class ForgotPasswordScreen extends React.Component<any, State> {
  formForgotPassRef: React.Ref<
    FormInstance<
      Record<string, any>,
      string,
      {
        [x: string]: string | undefined;
      }
    >
  >;

  formForgotSchemaModel: any;

  constructor(props: any) {
    super(props);

    this.state = {
      forgotPass: {
        email: '',
      },
    };

    this.formForgotPassRef = React.createRef();
    this.formForgotSchemaModel = Schema.Model({
      email: StringType()
        .isEmail('Invalid email address format')
        .isRequired('Please fill the required field'),
    });
  }

  private onFormValueChanged = (value: object, key: string): void =>
    this.setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));

  private onSendResetPassword = async (): Promise<void> => {
    const { t, navigate } = this.props;

    if (!_.invoke(this.formForgotPassRef, 'current.check')) {
      toaster.push(
        util.buildErrorMessage(
          t(constant.translation.profile.formErrorValidationMessageKey),
        ),
      );
      return;
    }

    try {
      const { forgotPass: payload } = this.state;
      await AuthAPI.forgotPassword(payload);

      toaster.push(
        util.buildMessage(
          t(constant.translation.profile.formSuccessSendResetPassKey),
        ),
      );

      navigate('/resend-password-link', {
        replace: true,
        state: { email: payload.email },
      });
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    } finally {
      this.setState({
        forgotPass: {
          email: '',
        },
      });
    }
  };

  render({ t } = this.props): JSX.Element | null {
    return (
      <div className={SCREEN_CLASS}>
        <div aria-hidden className={BACKGROUND_CLASS} />

        <div className={LAYOUT_CLASS}>
          <img src="bg-forgot-pass.png" alt="bg" className="w-3/5 max-w-xs" />

          <div className={PANEL_CLASS}>
            <header className="px-5 pb-5">
              <p
                // eslint-disable-next-line max-len
                className="mb-4 !leading-[1.2] text-center text-[32px] font-bold"
              >
                {t(constant.translation.forgotPass.pageCandidateTitleKey)}
              </p>
              <small
                // eslint-disable-next-line max-len
                className="block text-center text-base font-normal text-[#595959] opacity-75"
              >
                {t(constant.translation.forgotPass.pageCandidateSubTitleKey)}
              </small>
            </header>

            <Form
              key="forgot-pass-form"
              ref={this.formForgotPassRef}
              model={this.formForgotSchemaModel}
              onChange={(v: object) => this.onFormValueChanged(v, 'forgotPass')}
              formValue={_.get(this.state, 'forgotPass')}
              fluid
            >
              <Form.Group className="mt-10">
                <Form.ControlLabel>
                  {t(constants.translation.general.formEmailKey)}
                </Form.ControlLabel>
                <Form.Control
                  name="email"
                  placeholder="Enter your email address"
                />
              </Form.Group>

              <Button
                className="btn-tg-primary"
                block
                appearance="primary"
                onClick={this.onSendResetPassword}
              >
                {t(constant.translation.general.formForgotPasswordButtonKey)}
              </Button>

              <p className="mt-5 text-center font-normal">
                <a
                  href="/login"
                  // eslint-disable-next-line max-len
                  className="cursor-pointer font-normal text-[#212121] hover:!text-[#212121]"
                >
                  <FaArrowLeft /> Back to login
                </a>
              </p>
            </Form>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(withTranslation()(ForgotPasswordScreen));
